import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchAdminContent,
  getAdminToken,
  setAdminToken,
  isAdminSessionError,
} from '../content/contentApi';
import { useContent } from '../content/ContentProvider';
import { mergeWithDefaults } from './mergeContent';
import { useAdminContentPersist } from './hooks/useAdminContentPersist';
import { AdminPersistProvider } from './AdminPersistContext';
import EditableCaseStudiesPage from '../components/case-studies/EditableCaseStudiesPage';
import EditorTopBar from '../components/services/editable/EditorTopBar';
import './Admin.css';

/**
 * Dedicated full-viewport route for inline WYSIWYG editing of the (singleton)
 * Case Studies LISTING page — like HomePageEditor.jsx, this loads/auto-saves
 * the FULL site content (not just one section) since the page embeds the
 * existing CaseStudiesEditor/TestimonialsEditor admin panels (full CRUD for
 * cards/categories/stats/details/testimonial items) directly inline.
 */
export default function CaseStudiesPageEditor() {
  const navigate = useNavigate();
  const { refresh } = useContent();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(false);
  const prevLoadingRef = useRef(true);

  const handleSessionExpired = useCallback(() => {
    setAdminToken(null);
    navigate('/admin/login?reason=session', { replace: true });
  }, [navigate]);

  const persist = useAdminContentPersist({
    refresh,
    onError: (msg, err) => {
      if (isAdminSessionError(err)) {
        handleSessionExpired();
        return;
      }
      setError(msg);
    },
    onSuccess: setMessage,
  });
  const { setBaseline, onContentChange, persistNow, saving, pending } = persist;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = await fetchAdminContent();
      setContent(mergeWithDefaults(api));
    } catch (e) {
      if (isAdminSessionError(e)) {
        handleSessionExpired();
        return;
      }
      setError(e.message || 'Failed to load the Case Studies page.');
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login', { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  useEffect(() => {
    if (prevLoadingRef.current && !loading && content) {
      setBaseline(content);
    }
    prevLoadingRef.current = loading;
  }, [loading, content, setBaseline]);

  const handleContentChange = (next) => {
    setContent(next);
    onContentChange(next, setContent);
  };

  const handleCaseStudiesPageDataChange = (patch) => {
    handleContentChange({
      ...content,
      caseStudiesPage: { ...content.caseStudiesPage, ...patch },
    });
  };

  const handleSaveNow = async () => {
    setMessage('');
    setError('');
    try {
      await persistNow();
    } catch (e) {
      if (isAdminSessionError(e)) handleSessionExpired();
    }
  };

  if (loading || !content) {
    return (
      <div className="admin-root">
        <div className="admin-loading">
          <p>Loading page editor…</p>
        </div>
      </div>
    );
  }

  const statusLabel = saving ? 'Saving…' : pending ? 'Unsaved changes' : message || 'No changes yet';

  return (
    <div className="admin-root">
      <EditorTopBar
        title="Case Studies Page"
        preview={preview}
        onTogglePreview={() => setPreview((p) => !p)}
        onSave={handleSaveNow}
        saving={saving}
        statusLabel={statusLabel}
        onBack={() => navigate('/admin')}
      />
      {error ? <p className="admin-error" style={{ padding: '10px 20px', margin: 0 }}>{error}</p> : null}
      <AdminPersistProvider value={{ persistNow, saving, pending }}>
        <EditableCaseStudiesPage
          data={content.caseStudiesPage.data}
          styles={content.caseStudiesPage.styles}
          preview={preview}
          onChange={handleCaseStudiesPageDataChange}
          fullContent={content}
          onFullContentChange={handleContentChange}
        />
      </AdminPersistProvider>
    </div>
  );
}
